/**
 * Hong Kong local time (UTC+8, no DST) helpers.
 *
 * Timestamps travel as UTC ISO strings and are stored in UTC, which is correct.
 * These helpers exist for the DISPLAY boundary: a clinic in Hong Kong must always
 * read the Hong Kong calendar day, regardless of the browser's own timezone.
 * Without this, staff travelling or on a mis-set machine see the wrong date, and
 * `toISOString().split('T')[0]` returns the UTC day, which is yesterday for any
 * local time before 08:00 HKT.
 */

export const HK_TIME_ZONE = 'Asia/Hong_Kong';

/** Parses a value into a Date, returning null when it is missing or invalid. */
function toDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * The Hong Kong calendar date as YYYY-MM-DD.
 * Uses en-CA because that locale formats as YYYY-MM-DD natively.
 */
export function hkDateISO(value = new Date()) {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-CA', { timeZone: HK_TIME_ZONE });
}

/** The Hong Kong calendar date as DD/MM/YYYY, matching the PDF date fields. */
export function hkDateDMY(value = new Date()) {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-GB', {
    timeZone: HK_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

/**
 * Returns the Y/M/D of a timestamp as it reads in Hong Kong. Use this instead of
 * getFullYear/getMonth/getDate when the calendar day matters.
 */
export function hkParts(value = new Date()) {
  const date = toDate(value);
  if (!date) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: HK_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  return { year: get('year'), month: get('month'), day: get('day') };
}

/** Hong Kong time of day, e.g. "3:04 PM". */
export function hkTime(value = new Date()) {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleTimeString('en-US', {
    timeZone: HK_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Adds whole days to a Hong Kong calendar date and returns YYYY-MM-DD.
 * Safe for date-input defaults and `min` attributes, where UTC arithmetic would
 * be a day out for most of the Hong Kong working morning.
 */
export function hkDateISOPlusDays(days, value = new Date()) {
  const parts = hkParts(value);
  if (!parts) return '';
  // Build the date in UTC so the arithmetic cannot be shifted by the browser's
  // own offset, then read it back with UTC getters.
  const base = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  base.setUTCDate(base.getUTCDate() + days);
  const y = base.getUTCFullYear();
  const m = String(base.getUTCMonth() + 1).padStart(2, '0');
  const d = String(base.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * "Today, 3:04 PM" / "Yesterday, 3:04 PM" / "Aug 5, 3:04 PM", all in Hong Kong
 * time. Returns the em dash for empty values and the raw input if unparseable.
 */
export function hkRelativeDateTime(value) {
  if (!value) return '—';
  const date = toDate(value);
  if (!date) return value;

  const target = hkParts(date);
  const today = hkParts(new Date());
  const time = hkTime(date);

  if (target.year === today.year && target.month === today.month && target.day === today.day) {
    return `Today, ${time}`;
  }

  const yesterday = new Date(Date.UTC(today.year, today.month - 1, today.day));
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  if (
    target.year === yesterday.getUTCFullYear() &&
    target.month === yesterday.getUTCMonth() + 1 &&
    target.day === yesterday.getUTCDate()
  ) {
    return `Yesterday, ${time}`;
  }

  return `${date.toLocaleDateString('en-US', {
    timeZone: HK_TIME_ZONE,
    month: 'short',
    day: 'numeric'
  })}, ${time}`;
}

/** Full Hong Kong date and time, e.g. "2026-08-05, 3:04 PM". */
export function hkDateTime(value) {
  if (!value) return '—';
  const date = toDate(value);
  if (!date) return value;
  return `${hkDateISO(date)}, ${hkTime(date)}`;
}
